import React, { useState } from 'react';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { ChatMessage, editChatMessage, deleteChatMessage } from '../../store/slices/chatSlice';
import { getChatDisplayName } from '../../utils/chatUtils';
import ChatMessageReactions from './ChatMessageReactions';
import Button from '../UI/Button';
import UserInfo from '../UI/UserInfo';

interface ChatMessageItemProps {
  message: ChatMessage;
  projectId: string;
  onReply?: (message: ChatMessage) => void;
}

const ChatMessageItem: React.FC<ChatMessageItemProps> = ({ message, projectId, onReply }) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(message.content);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isOwnMessage = user?.id && message.userId && (
    typeof message.userId === 'string' 
      ? message.userId === user.id 
      : (message.userId as any).profile?.displayName
  );

  // Check if this is the user's last message (for edit permission)
  const canEdit = isOwnMessage && !message.edited; // Simplified - backend will enforce last message only

  const userInfo = typeof message.userId === 'object' ? message.userId : message.userId;
  const displayName = getChatDisplayName(userInfo);

  const handleEdit = () => {
    setEditContent(message.content);
    setIsEditing(true);
  };

  const handleSaveEdit = async () => {
    if (!editContent.trim() || isUpdating) return;
    setIsUpdating(true);
    try {
      await dispatch(editChatMessage({ messageId: message.id || message._id || '', content: editContent.trim() }));
      setIsEditing(false);
    } catch (error) {
      console.error('Error editing message:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setEditContent(message.content);
    setIsEditing(false);
  };

  const handleDelete = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      await dispatch(deleteChatMessage(message.id || message._id || ''));
    } catch (error) {
      console.error('Error deleting message:', error);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const parentMessage = typeof message.parentId === 'object' ? message.parentId : null;
  const parentDisplayName = parentMessage
    ? getChatDisplayName(
        typeof parentMessage.userId === 'object' ? parentMessage.userId : undefined
      )
    : null;

  return (
    <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} group`}>
      <div className={`flex gap-2 max-w-xs lg:max-w-md ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'}`}>
        {/* Avatar */}
        {!isOwnMessage && (
          <div className="flex-shrink-0">
            <UserInfo user={userInfo} size="sm" showBadges={false} showReputation={false} avatarOnly={true} />
          </div>
        )}

        {/* Message content */}
        <div className={`flex-1 ${isOwnMessage ? 'items-end' : 'items-start'} flex flex-col`}>
          {/* User name and badges (for other users) */}
          {!isOwnMessage && (
            <div className="mb-1 px-1">
              <UserInfo user={userInfo} size="sm" showBadges={true} showReputation={true} />
            </div>
          )}

          {/* Parent message (reply) */}
          {parentMessage && (
            <div className="mb-1 px-3 py-1 bg-[var(--color-neutral-100)] border-l-2 border-[var(--color-brand-primary)] rounded text-xs text-[var(--color-subtext-color)] italic">
              <span className="font-medium">{parentDisplayName}:</span> {parentMessage.content}
            </div>
          )}

          {/* Message bubble */}
          <div
            className={`px-4 py-2 rounded-lg ${
              isOwnMessage
                ? 'bg-[var(--color-brand-primary)] text-white'
                : 'bg-[var(--color-neutral-100)] text-[var(--color-default-font)]'
            }`}
          >
            {isEditing ? (
              <div className="space-y-2">
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="w-full px-2 py-1 border border-[var(--color-neutral-border)] rounded text-sm text-[var(--color-default-font)] bg-[var(--color-default-background)]"
                  rows={3}
                  autoFocus
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleSaveEdit} isLoading={isUpdating} disabled={!editContent.trim()}>
                    Save
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCancelEdit} disabled={isUpdating}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                {(message.edited || message.reactionSummary) && (
                  <div className="flex items-center gap-2 mt-2">
                    {message.edited && (
                      <span className={`text-xs ${isOwnMessage ? 'text-white/70' : 'text-[var(--color-subtext-color)]'}`}>
                        (edited)
                      </span>
                    )}
                    {message.reactionSummary && Object.keys(message.reactionSummary).length > 0 && (
                      <ChatMessageReactions
                        messageId={message.id || message._id || ''}
                        reactionSummary={message.reactionSummary}
                        userReactions={message.userReactions}
                      />
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Timestamp and actions */}
          <div className={`flex items-center gap-2 mt-1 px-1 ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'}`}>
            <p className={`text-xs ${isOwnMessage ? 'text-[var(--color-subtext-color)]' : 'text-[var(--color-subtext-color)]'}`}>
              {new Date(message.createdAt).toLocaleTimeString()}
            </p>
            {isOwnMessage && !isEditing && (
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {canEdit && (
                  <button
                    onClick={handleEdit}
                    className="text-xs text-[var(--color-subtext-color)] hover:text-[var(--color-default-font)]"
                    aria-label="Edit message"
                  >
                    Edit
                  </button>
                )}
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="text-xs text-[var(--color-error-600)] hover:text-[var(--color-error-700)]"
                  aria-label="Delete message"
                >
                  Delete
                </button>
                {onReply && (
                  <button
                    onClick={() => onReply(message)}
                    className="text-xs text-[var(--color-brand-primary)] hover:text-[var(--color-brand-700)]"
                    aria-label="Reply to message"
                  >
                    Reply
                  </button>
                )}
              </div>
            )}
            {!isOwnMessage && onReply && (
              <button
                onClick={() => onReply(message)}
                className="text-xs text-[var(--color-subtext-color)] hover:text-[var(--color-default-font)] opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Reply to message"
              >
                Reply
              </button>
            )}
          </div>

          {/* Delete confirmation */}
          {showDeleteConfirm && (
            <div className="mt-2 p-2 bg-[var(--color-error-50)] border border-[var(--color-error-200)] rounded text-xs">
              <p className="text-[var(--color-error-800)] mb-2">Delete this message?</p>
              <div className="flex gap-2">
                <Button size="sm" variant="danger" onClick={handleDelete} isLoading={isDeleting}>
                  Delete
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatMessageItem;

